import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Fade,
  InputAdornment,
  IconButton,
  Link,
  Grid,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Lock,
  PhoneAndroid,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { adminRegister } from '../services/auth';
import logo from '../assets/images/logo.png';

const Register = () => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validatePhone = (phoneNumber) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phoneNumber') {
      let raw = String(value).trim();
      if (raw.startsWith('+91')) raw = raw.slice(3);
      else if (raw.startsWith('+')) raw = raw.slice(1);

      const digits = raw.replace(/\D/g, '');
      let cleanPhone = digits.slice(0, 10);
      if (digits.length >= 12 && digits.startsWith('91')) {
        cleanPhone = digits.slice(2, 12);
      } else if (digits.length === 11 && digits.startsWith('0')) {
        cleanPhone = digits.slice(1, 11);
      }
      setFormData((prev) => ({
        ...prev,
        [name]: cleanPhone,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    // Clear errors on change
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    setError('');
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await adminRegister({
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        name: formData.name,
      });
      
      setSuccess('Registration successful! Redirecting to login...');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Fade in={true} timeout={800}>
        <Paper
          elevation={6}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.08)'
          }}
        >
          {/* Back Button */}
          <Box sx={{ alignSelf: 'flex-start', mb: 2 }}>
            <Button
              component={RouterLink}
              to="/login"
              startIcon={<ArrowBack />}
              sx={{
                textTransform: 'none',
                color: '#6b7280',
                '&:hover': { color: '#52ad83', background: 'rgba(82, 173, 131, 0.08)' },
              }}
            >
              Back to Login
            </Button>
          </Box>

          {/* Logo Section */}
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              width: 150,
              height: 'auto',
              borderRadius: 2,
              mb: 3,
              objectFit: 'contain'
            }}
          />

          <Typography
            component="h1"
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Admin Registration
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: '#6b7280' }}>
            Create a new admin account
          </Typography>

          {/* Error Alert */}
          <Fade in={!!error}>
            <Alert
              severity="error"
              sx={{
                width: '100%',
                mb: 2,
                borderRadius: 2
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          </Fade>

          {/* Success Alert */}
          <Fade in={!!success}>
            <Alert
              severity="success"
              sx={{
                width: '100%',
                mb: 2,
                borderRadius: 2
              }}
            >
              {success}
            </Alert>
          </Fade>

          {/* Registration Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="Enter your full name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#52ad83',
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              placeholder="Enter 10-digit phone number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneAndroid color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#52ad83',
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              error={!!errors.password}
              helperText={errors.password}
              placeholder="Enter your password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#6b7280' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#52ad83',
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              placeholder="Confirm your password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#52ad83',
                  },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || success}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AdminPanelSettings />}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                boxShadow: '0 4px 15px rgba(82, 173, 131, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3d8f64 0%, #52ad83 100%)',
                  boxShadow: '0 6px 20px rgba(82, 173, 131, 0.4)',
                },
                '&:disabled': {
                  background: '#d1d5db',
                  boxShadow: 'none',
                  color: '#9ca3af'
                }
              }}
            >
              {loading ? 'Creating Account...' : 'Create Admin Account'}
            </Button>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, color: '#6b7280', textAlign: 'center' }}>
            Already have an account?{' '}
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                color: '#52ad83',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  color: '#3d8f64',
                  textDecoration: 'underline',
                },
              }}
            >
              Sign In
            </Link>
          </Typography>
        </Paper>
      </Fade>
    </Container>
  );
};

export default Register;

